import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FolderKanban } from 'lucide-react';
import { ORG_BASE } from '@/pages/services/organizations/organizations-service';
import { Container, PageHeader, Breadcrumbs, BreadcrumbItem, Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  Input,
  Textarea,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useProject,
  useUpdateProject,
  useArchiveProject,
  useRestoreProject,
  useDeleteProject,
} from '@/api/projects';
import { ProjectTeamsTab } from './ProjectTeamsTab';
import { ProjectResourcesTab } from './ProjectResourcesTab';
import {
  PROJECT_STATUS_TONE,
  PROJECT_STATUS_LABEL,
  canManage as canManageRole,
  formatDateTime,
} from './shared';

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <dt style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{label}</dt>
      <dd
        style={{
          margin: 0,
          fontSize: 'var(--vn-font-size-md)',
          fontFamily: mono ? 'var(--vn-font-mono, ui-monospace, monospace)' : undefined,
        }}
      >
        {value || '—'}
      </dd>
    </div>
  );
}

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { show } = useToast();

  const { data: project, isLoading } = useProject(projectId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confirm, setConfirm] = useState<'archive' | 'restore' | 'delete' | null>(null);

  // Sync the edit form whenever the project loads/changes.
  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description ?? '');
    }
  }, [project]);

  const canManage = canManageRole(project?.user_role);
  const isOwner = project?.user_role === 'owner';
  const dirty =
    !!project && (name.trim() !== project.name || description !== (project.description ?? ''));

  const update = useUpdateProject(projectId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Project updated' }),
    onError: () => show({ tone: 'danger', title: 'Could not update project' }),
  });
  const archive = useArchiveProject({
    onSuccess: () => {
      show({ tone: 'success', title: 'Project archived' });
      setConfirm(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not archive project' }),
  });
  const restore = useRestoreProject({
    onSuccess: () => {
      show({ tone: 'success', title: 'Project restored' });
      setConfirm(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not restore project' }),
  });
  const remove = useDeleteProject({
    onSuccess: () => {
      show({ tone: 'success', title: 'Project deleted' });
      navigate(`${ORG_BASE}/projects`);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete project' }),
  });

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 36, width: 240, marginBottom: 24 }} />
        <Skeleton style={{ height: 220 }} />
      </Container>
    );
  }

  if (!project) {
    return (
      <Container size="xl">
        <EmptyState
          icon={<FolderKanban size={28} />}
          title="Project not found"
          description="It may have been deleted, or you don't have access to it."
          actions={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`${ORG_BASE}/projects`)}
            >
              Back to projects
            </Button>
          }
        />
      </Container>
    );
  }

  function handleSave() {
    if (!name.trim()) return;
    update.mutate({ name: name.trim(), description: description.trim() });
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<FolderKanban size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild>
              <Link to={`${ORG_BASE}/projects`}>Projects</Link>
            </BreadcrumbItem>
            <BreadcrumbItem current>{project.name}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={project.name}
        actions={
          <StatusPill tone={PROJECT_STATUS_TONE[project.status]}>
            {PROJECT_STATUS_LABEL[project.status]}
          </StatusPill>
        }
      />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="teams">Teams</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
        </TabsList>

        {/* ---------------- Overview ---------------- */}
        <TabsContent value="overview">
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {/* Basic information — editable when user can manage */}
            <Section
              title="Basic information"
              description={canManage ? 'Edit your project name and description.' : 'Project name and description.'}
              style={{ marginBottom: 0 }}
            >
              <SettingRow
                title="Name & description"
                description="A short, memorable name and an optional note about this project."
              >
                <div
                  style={{
                    display: 'grid',
                    gap: 'var(--vn-space-4)',
                    maxWidth: 480,
                    marginTop: 'var(--vn-space-3)',
                  }}
                >
                  <Field label="Project name">
                    {(p) => (
                      <Input
                        {...p}
                        value={name}
                        disabled={!canManage}
                        onChange={(e) => setName(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Description">
                    {(p) => (
                      <Textarea
                        {...p}
                        rows={3}
                        value={description}
                        disabled={!canManage}
                        placeholder={canManage ? 'What is this project for?' : '—'}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </SettingRow>
              {canManage && (
                <ActionBar
                  sticky={false}
                  start={
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!dirty || update.isPending}
                      onClick={() => {
                        setName(project.name);
                        setDescription(project.description ?? '');
                      }}
                    >
                      Reset
                    </Button>
                  }
                  end={
                    <Button
                      size="sm"
                      disabled={!dirty || !name.trim() || update.isPending}
                      onClick={handleSave}
                    >
                      {update.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                  }
                />
              )}
            </Section>

            {/* Read-only project details */}
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardBody>
                <dl
                  style={{
                    display: 'grid',
                    gap: 'var(--vn-space-4)',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    margin: 0,
                  }}
                >
                  <DetailRow label="Project ID" value={project.id} mono />
                  <DetailRow label="Slug" value={project.slug} mono />
                  <DetailRow label="Owner" value={project.owner_email} />
                  <DetailRow label="Organization" value={project.organization_id} mono />
                  <DetailRow label="Created" value={formatDateTime(project.created_at)} />
                  <DetailRow label="Last updated" value={formatDateTime(project.updated_at)} />
                </dl>
              </CardBody>
            </Card>

            {/* Danger zone — owner only */}
            {isOwner && (
              <>
                <Section
                  title="Project status"
                  description={
                    project.status === 'archived'
                      ? 'This project is archived and hidden from the active list.'
                      : 'Archive to hide this project. You can restore it later.'
                  }
                  style={{ marginBottom: 0 }}
                >
                  <SettingRow
                    title={project.status === 'archived' ? 'Restore project' : 'Archive project'}
                    description={
                      project.status === 'archived'
                        ? 'Bring this project back into the active list.'
                        : 'Hide this project without deleting it.'
                    }
                    control={
                      project.status === 'archived' ? (
                        <Button variant="secondary" size="sm" onClick={() => setConfirm('restore')}>
                          Restore project
                        </Button>
                      ) : (
                        <Button variant="secondary" size="sm" onClick={() => setConfirm('archive')}>
                          Archive project
                        </Button>
                      )
                    }
                  />
                </Section>

                <Section title="Danger zone" description="Destructive actions for this project." style={{ marginBottom: 0 }}>
                  <SettingRow
                    title="Delete this project"
                    description="Permanently delete this project. This action cannot be undone."
                    control={
                      <Button variant="danger" size="sm" onClick={() => setConfirm('delete')}>
                        Delete project
                      </Button>
                    }
                  />
                </Section>
              </>
            )}
          </div>
        </TabsContent>

        {/* ---------------- Teams ---------------- */}
        <TabsContent value="teams">
          <ProjectTeamsTab projectId={project.id} canManage={canManage} actorIsOwner={isOwner} />
        </TabsContent>

        {/* ---------------- Resources ---------------- */}
        <TabsContent value="resources">
          <ProjectResourcesTab projectId={project.id} canManage={canManage} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirm === 'archive'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Archive project?"
        description={`"${project.name}" will be hidden but can be restored later.`}
        confirmLabel="Archive"
        loading={archive.isPending}
        onConfirm={() => archive.mutate(project.id)}
      />
      <ConfirmDialog
        open={confirm === 'restore'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Restore project?"
        description={`"${project.name}" will return to your active projects.`}
        confirmLabel="Restore"
        loading={restore.isPending}
        onConfirm={() => restore.mutate(project.id)}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Delete project?"
        description={`This permanently deletes "${project.name}". This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={project.name}
        loading={remove.isPending}
        onConfirm={() => remove.mutate(project.id)}
      />
    </Container>
  );
}
